import { Box, Flex, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useCallback, useMemo } from "react";
import autoFormDefaultValues, { buildDataTransformer, buildFormSchema } from "features/PendingNewHire/Form/buildForm";
import { yupResolver } from "@hookform/resolvers/yup";
import LoadingDots from "components/Loading";
import { setNewNewHireJourneyInQueryClient, useNewHireJourneyDetailQuery, useUpdateNewHireJourney } from "features/NewHireJourney/hooks";
import { AutoFormSection } from "models/autoForm";
import { useQueryClient } from "@tanstack/react-query";
import DynamicForm from "features/PendingNewHire/Form/DynamicForm";
import ProceedCancelButtons from "components/Button/ProceedCancelButtons";

interface FormSchema {
  sections: AutoFormSection[];
}

interface NewHireAutoFormProps {
  newHireId: string;
  formSchema: FormSchema;
  onSuccess?: (res: any) => void;
  onClose?: () => void;
}

export default function NewHireAutoForm({ newHireId, formSchema, onSuccess, onClose = () => {} }: NewHireAutoFormProps) {
  const { data: newHireJourney, isFetched } = useNewHireJourneyDetailQuery(newHireId);
  const { mutate } = useUpdateNewHireJourney(newHireId, false);
  const queryClient = useQueryClient();
  const toast = useToast();

  const transformData = useMemo(() => buildDataTransformer(formSchema?.sections), [formSchema?.sections]);
  const schema = useMemo(() => buildFormSchema(formSchema?.sections), [formSchema?.sections]);

  const onSubmit = useCallback((data: any) => {
    const transformedData = transformData(data);
    mutate(transformedData, {
      onSuccess: (res: any) => {
        setNewNewHireJourneyInQueryClient(queryClient, res);
        onSuccess?.(res);
        toast({
          title: "Updated.",
          status: "success",
        });
        onClose?.();
      },
    });
  }, [mutate, onClose, onSuccess, queryClient, toast, transformData]);

  const form = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    values: autoFormDefaultValues(formSchema?.sections, newHireJourney),
    resetOptions: {
      keepDirtyValues: true,
      keepErrors: true,
      keepTouched: true,
    },
  });
  const {
    handleSubmit,
  } = form;
  return !newHireJourney ? (
    <Box mt="16">
      <LoadingDots />
    </Box>
  ) : (
    <Flex direction="column">
      <DynamicForm
        form={form}
        sections={formSchema?.sections}
        isLoaded={isFetched}
      />
      <Flex px="4" justify="flex-end" gap="2">
        <ProceedCancelButtons handleSubmit={handleSubmit(onSubmit)} onClose={onClose} proceedText="Save" />
      </Flex>
    </Flex>
  );
}
